import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

const ContactPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pageContent, setPageContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchContactPageData();
  }, []);

  const fetchContactPageData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase?.from('page_content')?.select('*')?.eq('page_name', 'contact')?.eq('is_active', true)?.order('display_order', { ascending: true });

      if (data) setPageContent(data);
    } catch (err) {
      console.error('Error fetching contact page data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e?.target?.name]: e?.target?.value });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (submitting) return;
    
    try {
      setSubmitting(true);

      const { error: fnError } = await supabase?.functions?.invoke('send-contact-notification', {
        body: {
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          userId: user?.id || null
        }
      });

      if (fnError) throw fnError;

      toast.success('Thank you! Your message has been sent successfully.');
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    } catch (err) {
      console.error('Error submitting contact form:', err);
      toast.error('Failed to send message. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const heroSection = pageContent?.find(c => c?.section_type === 'hero');
  const contactInfo = pageContent?.find(c => c?.section_type === 'contact_info');

  const contactMethods = [
    {
      icon: 'Mail',
      title: 'Email',
      value: 'info@firecaseiq.com',
      description: 'Send us an email anytime',
    },
    {
      icon: 'Mail',
      title: 'Enterprise Sales',
      value: 'sales@firecaseiq.com',
      description: 'For enterprise inquiries',
    },
    {
      icon: 'MapPin',
      title: 'Office',
      value: '5448 Apex Peakway Suite 157',
      description: 'Apex NC 27502',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b border-border py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center">
            <img src="/assets/images/logo.png" alt="FireCaseIQ" className="h-10 object-contain" />
          </button>
          <button
            onClick={() => navigate('/')}
            className="text-sm text-muted-foreground hover:text-primary transition-smooth"
          >
            Back to Home
          </button>
        </div>
      </header>

      {loading && (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      )}

      {!loading && (
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-foreground mb-4">
              {heroSection?.section_title || 'Get in Touch'}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {heroSection?.section_content || 'Have questions about our services? We are here to help. Reach out to our team.'}
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            {contactMethods?.map((method, index) => (
              <div key={index} className="bg-card rounded-lg p-6 border border-border text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name={method?.icon} size={24} color="#FFFFFF" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">{method?.title}</h3>
                <p className="text-primary font-medium mb-1">{method?.value}</p>
                <p className="text-sm text-muted-foreground">{method?.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-card rounded-xl p-8 border border-border max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-foreground mb-6">Send us a Message</h2>
            {submitted ? (
              <div className="bg-success/10 border border-success text-success px-4 py-3 rounded-lg text-center">
                <Icon name="CheckCircle" size={24} className="mx-auto mb-2" />
                <p className="font-medium">Thank you! Your message has been sent successfully.</p>
                <p className="text-sm">We will get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData?.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData?.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData?.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData?.message}
                    onChange={handleChange}
                    rows={6}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Icon name="Loader" size={20} className="animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactPage;